import {
  READABLE_ID_FIELD,
  ID_OFFSET,
  READABLE_ID_MAX_LENGTH
} from 'config/client'
import { status as productStatus } from '@jarvis.oddle/core/tools/product'
import { min } from '@jarvis.oddle/core/tools/array'

export const makeReadableId = (
  id,
  name,
  idOffset = ID_OFFSET,
  readableIdMaxLength = READABLE_ID_MAX_LENGTH
) =>
  `${id.slice(-1 * idOffset)}-${name
    .toLowerCase()
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .replace(/ /g, '-')}`.substring(0, readableIdMaxLength)

export const injectReadableIds = (groups) => {
  return (groups || []).map((group) => {
    group.items = (group.items || []).map((item) => {
      item[READABLE_ID_FIELD] = makeReadableId(item.id, item.name)
      return item
    })
    return group
  })
}

export const findItem = (groups, checker) => {
  let item = null
  groups.some((g) => {
    const found = g.items.find(checker)
    if (found) {
      item = found
      return true
    }
  })
  return item
}

export const findVariantsItemFromGroups = (groups, value) =>
  findItem(groups, (item) =>
    item.variants.some((variant) => variant.id === value)
  )

export const getFullProductDetails = (menu, item) => {
  const fullItem = findVariantsItemFromGroups(menu, item.itemReference)
  return fullItem ? { ...item, details: fullItem } : item
}

export const getOrderItemQuantity = (orderItems) =>
  orderItems.reduce((sum, order) => +order.quantity + sum, 0)

export const getDisplayPriceFromItem = (
  t,
  { variants = [], price },
  formatMoney = (x) => x
) => {
  const displayPrice =
    typeof price === 'number'
      ? formatMoney(price)
      : formatMoney(price || getItemPrice({ variants }))
  return variants.length > 1
    ? { preposition: t('From'), displayPrice }
    : { preposition: '', displayPrice }
}

export const getItemPrice = ({ variants = [] }) =>
  variants.length > 1
    ? min(variants.map((v) => v.price))
    : // NOTE: It's guaranteed that variants.length >= 1.
      // But we just do the fallback here for extra safety (as old code had this fallback).
      parseFloat((variants[0] || {}).price)

export const getOrderItem = (
  product,
  totalQuantity,
  selectedVariant,
  subitemsMap
) => {
  const subitems = Object.keys(subitemsMap || {})
    .map((subitemId) => {
      const variants = Object.keys(subitemsMap[subitemId] || {})
        .map((variantId) => ({
          variantId,
          quantity: subitemsMap[subitemId][variantId]
        }))
        .filter(({ quantity }) => quantity > 0)

      return { subitemId, variants }
    })
    .filter(({ variants }) => variants.length > 0)

  return {
    variantId: selectedVariant || product.variants[0].id,
    quantity: totalQuantity,
    subitems
  }
}

export const getProductButtonProps = (
  t,
  product,
  category,
  storeItemAvailability
) => {
  const item = category.status === productStatus.AVAILABLE ? product : category

  const { isUnavailableAtSelectedStore, isUnavailableAtAllStores } =
    storeItemAvailability

  if (isUnavailableAtAllStores || isUnavailableAtSelectedStore) {
    return { text: t('Unavailable'), disabled: true }
  }

  if (item.status === productStatus.UNAVAILABLE) {
    // WARNING! please do not use isEmpty() as null and '' have different meaning in this context
    if (item.availableDays === '') {
      return { text: t('Unavailable'), disabled: true }
    } else {
      return { text: t('Pre-order'), disabled: false }
    }
  } else {
    return { text: t('Add'), disabled: false }
  }
}

export const getProductIdFromRoute = (route) => {
  const shouldRender = (route || {}).path && (route.path || [])[0] === 'product'
  const productId = shouldRender ? route.path[1] : null
  return productId
}

export const getTwoLetterCountryCode = (country) => {
  const regions = {
    singapore: 'sg',
    taiwan: 'tw',
    malaysia: 'my',
    vietnam: 'vn'
  }
  return regions[country]
}

export const makeRestaurantSchema = ({
  menu,
  meta,
  siteSettingData,
  store,
  currentUrl,
  sections
}) => {
  // this default logo will be replaced if the customer upload their logo.
  // default logo is required to pass schema markup
  const defaultFavicon =
    'https://cdn.oddle.me/wp-content/uploads/2020/09/small-oddle-logo-transparent.png'
  const homePageUrl = new URL(`https:${currentUrl}`)
  const locations = []
  const menus = []
  store.data.forEach((s) => {
    // spliting address to get postal code, address country and street address
    const subAddress = s.address.split(',')
    // remove empty available days
    const getAvailableDays = (s.storeHours || []).filter(
      (days) => days.availableDays !== ''
    )
    // make opening hours become like this format Mon - Fri 12:15 AM - 08:00 PM
    const openingHours = getAvailableDays.map((days) => {
      const result = []
      const availableDays = days.availableDays.split(',')
      const openingDays = `${availableDays[0].trim()} - ${availableDays[
        availableDays.length - 1
      ].trim()}`
      result.push(`${openingDays} ${days.availableTimes}`)
      return result
    })
    const location = {
      '@type': 'restaurant',
      parentOrganization: {
        name: meta.menu.name
      },
      name: s.name,
      image: siteSettingData.favicon || defaultFavicon,
      telephone: s.phone,
      currenciesAccepted: meta.currencyCode,
      priceRange: '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${subAddress[0]} ${subAddress[1]}`,
        addressLocality: subAddress[2].trim(),
        addressCountry: subAddress[2].trim(),
        addressRegion: getTwoLetterCountryCode(
          subAddress[2].trim().toLowerCase()
        ),
        postalCode: subAddress[3]
      },
      serveCuisine: [],
      openingHours
    }
    locations.push(location)
  })
  menu?.forEach((menuType) => {
    const hasMenu = {
      '@type': 'MenuSection',
      name: menuType.name,
      hasMenuItem: []
    }
    menuType.items.forEach((item) => {
      // get base subitems name to include in the description on @type menuItem section.
      const subitems = item.subitems.map(
        (subitem) =>
          `- ${subitem.variants[0] ? subitem.variants[0].variantName : ''}`
      )
      hasMenu['hasMenuItem'].push({
        '@type': 'MenuItem',
        name: item.name,
        description: `includes: ${subitems.length ? subitems.join(' ') : ''}`,
        offers: {
          '@type': 'offer',
          // using base price
          price: item.variants[0] ? item.variants[0].price : null,
          priceCurrency: meta.currencyCode
        }
      })
    })

    menus.push(hasMenu)
  })
  return {
    // schema for restaurants markup
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: meta.menu.name,
    url: (sections.header || {}).homepageUrl || `https://${homePageUrl.host}`,
    logo: siteSettingData.favicon || defaultFavicon,
    location: locations,
    hasMenu: {
      '@type': 'Menu',
      name: 'Delivery Menu',
      description: siteSettingData.homePageMetaDescription,
      hasMenuSection: menus
    }
  }
}
